import type { StateCreator } from 'zustand';
import type {
  Alert,
  AlertRule,
  AlertConfig,
  AlertStatistics,
  AlertSeverity,
  AlertCategory,
  AlertStatus,
  AlertContext,
} from '../../types/alerts';
import { DEFAULT_ALERT_CONFIG } from '../../types/alerts';

// ============================================================================
// ALERT SLICE
// ============================================================================

export interface AlertSlice {
  // State
  alerts: Alert[];
  alertRules: AlertRule[];
  alertConfig: AlertConfig;

  // Actions - Alert Management
  addAlert: (alert: Omit<Alert, 'id' | 'detectedAt' | 'priority'>) => void;
  acknowledgeAlert: (id: string, userId?: string) => void;
  resolveAlert: (id: string) => void;
  dismissAlert: (id: string) => void;
  clearOldAlerts: () => void;

  // Actions - Rule Management
  toggleAlertRule: (ruleId: string, enabled: boolean) => void;
  updateAlertConfig: (config: Partial<AlertConfig>) => void;

  // Actions - Detection
  detectAlerts: () => void;
  checkRule: (rule: AlertRule, context: AlertContext) => boolean;

  // Computed
  getActiveAlerts: () => Alert[];
  getAlertsByCategory: (category: AlertCategory) => Alert[];
  getAlertsBySeverity: (severity: AlertSeverity) => Alert[];
  getAlertStatistics: () => AlertStatistics;
}

export const createAlertSlice: StateCreator<any, [], [], AlertSlice> = (set, get) => ({
  // Initial state
  alerts: [],
  alertRules: [],
  alertConfig: DEFAULT_ALERT_CONFIG,

  // Add a new alert
  addAlert: (alertData) => {
    const newAlert: Alert = {
      ...alertData,
      id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      detectedAt: new Date(),
      status: 'active',
      priority: alertData.impact * alertData.urgency,
    };

    set({ alerts: [...get().alerts, newAlert] });

    // Show notification if enabled
    if (get().alertConfig.showNotifications) {
      get().addNotification(
        alertData.severity === 'critical' ? 'error' : alertData.severity === 'warning' ? 'warning' : 'info',
        newAlert.title
      );
    }
  },

  // Acknowledge an alert
  acknowledgeAlert: (id, userId) => {
    const alerts = get().alerts;
    const updatedAlerts = alerts.map((alert: any) =>
      alert.id === id
        ? {
            ...alert,
            status: 'acknowledged' as AlertStatus,
            acknowledgedAt: new Date(),
            acknowledgedBy: userId,
          }
        : alert
    );

    set({ alerts: updatedAlerts });
  },

  // Resolve an alert
  resolveAlert: (id) => {
    const alerts = get().alerts;
    const updatedAlerts = alerts.map((alert: any) =>
      alert.id === id
        ? {
            ...alert,
            status: 'resolved' as AlertStatus,
            resolvedAt: new Date(),
          }
        : alert
    );

    set({ alerts: updatedAlerts });
  },

  // Dismiss an alert
  dismissAlert: (id) => {
    const alerts = get().alerts;
    const updatedAlerts = alerts.map((alert: any) =>
      alert.id === id
        ? {
            ...alert,
            status: 'dismissed' as AlertStatus,
          }
        : alert
    );

    set({ alerts: updatedAlerts });
  },

  // Clear old alerts (older than configured days)
  clearOldAlerts: () => {
    const { alerts, alertConfig } = get();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - alertConfig.dismissAfterDays);

    const activeAlerts = alerts.filter((alert: any) => {
      if (alert.status === 'active') return true; // Keep all active alerts
      const alertDate = new Date(alert.detectedAt);
      return alertDate > cutoffDate; // Keep recent non-active alerts
    });

    set({ alerts: activeAlerts });
  },

  // Toggle an alert rule
  toggleAlertRule: (ruleId, enabled) => {
    const alertRules = get().alertRules;
    const updatedRules = alertRules.map((rule: any) =>
      rule.id === ruleId ? { ...rule, enabled } : rule
    );

    set({ alertRules: updatedRules });
  },

  // Update alert configuration
  updateAlertConfig: (config) => {
    set({
      alertConfig: {
        ...get().alertConfig,
        ...config,
      },
    });
  },

  // Main detection function - scans all enabled rules
  detectAlerts: () => {
    const { alertRules, alertConfig, nodes, edges, members, risks, kanbanBoard, getStatistics, criticalPath } = get();

    if (!alertConfig.enabled) return;

    const context: AlertContext = {
      nodes,
      edges,
      members,
      risks,
      kanbanCards: kanbanBoard?.columns?.flatMap((col: any) => col.cards) || [],
      statistics: getStatistics(),
      criticalPath: criticalPath || [],
      currentDate: new Date(),
      projectStartDate: get().currentProject?.startDate,
      projectEndDate: get().currentProject?.endDate,
    };

    // Check each enabled rule
    alertRules.forEach((rule: any) => {
      if (!rule.enabled) return;
      if (!alertConfig.enabledCategories[rule.category]) return;

      // Check cooldown
      if (rule.lastTriggered && rule.cooldownMinutes) {
        const minutesSinceLastTrigger =
          (new Date().getTime() - new Date(rule.lastTriggered).getTime()) / (1000 * 60);
        if (minutesSinceLastTrigger < rule.cooldownMinutes) return;
      }

      // Check if rule condition is met
      const shouldTrigger = get().checkRule(rule, context);

      if (shouldTrigger) {
        // Update last triggered
        get().toggleAlertRule(rule.id, true);
        set({
          alertRules: get().alertRules.map((r: any) =>
            r.id === rule.id ? { ...r, lastTriggered: new Date() } : r
          ),
        });
      }
    });
  },

  // Check a specific rule condition
  checkRule: (rule, context) => {
    // This is implemented via imported rule conditions
    // Each rule has a condition function that takes the context
    if (rule.condition) {
      return rule.condition(context);
    }
    return false;
  },

  // Get active alerts
  getActiveAlerts: () => {
    return get().alerts.filter((alert: any) => alert.status === 'active');
  },

  // Get alerts by category
  getAlertsByCategory: (category) => {
    return get().alerts.filter((alert: any) => alert.category === category && alert.status === 'active');
  },

  // Get alerts by severity
  getAlertsBySeverity: (severity) => {
    return get().alerts.filter((alert: any) => alert.severity === severity && alert.status === 'active');
  },

  // Get alert statistics
  getAlertStatistics: () => {
    const alerts = get().alerts;

    const totalActive = alerts.filter((a: any) => a.status === 'active').length;
    const totalAcknowledged = alerts.filter((a: any) => a.status === 'acknowledged').length;
    const totalResolved = alerts.filter((a: any) => a.status === 'resolved').length;
    const totalDismissed = alerts.filter((a: any) => a.status === 'dismissed').length;

    // Count by severity
    const bySeverity: Record<AlertSeverity, number> = {
      info: 0,
      warning: 0,
      critical: 0,
      success: 0,
    };
    alerts.forEach((alert: any) => {
      if (alert.status === 'active') {
        bySeverity[alert.severity as AlertSeverity]++;
      }
    });

    // Count by category
    const byCategory: Record<AlertCategory, number> = {
      schedule: 0,
      resource: 0,
      budget: 0,
      quality: 0,
      risk: 0,
      dependency: 0,
      performance: 0,
      team: 0,
    };
    alerts.forEach((alert: any) => {
      if (alert.status === 'active') {
        byCategory[alert.category as AlertCategory]++;
      }
    });

    // Get critical alerts (sorted by priority)
    const criticalAlerts = alerts
      .filter((a: any) => a.status === 'active' && a.severity === 'critical')
      .sort((a: any, b: any) => b.priority - a.priority);

    // Get recent alerts
    const recentAlerts = alerts
      .filter((a: any) => a.status === 'active')
      .sort((a: any, b: any) => new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime())
      .slice(0, 10);

    // Calculate average resolution time
    const resolvedAlerts = alerts.filter((a: any) => a.status === 'resolved' && a.resolvedAt);
    const avgResolutionTime =
      resolvedAlerts.length > 0
        ? resolvedAlerts.reduce((sum: any, alert: any) => {
            const detectedTime = new Date(alert.detectedAt).getTime();
            const resolvedTime = new Date(alert.resolvedAt!).getTime();
            return sum + (resolvedTime - detectedTime) / (1000 * 60 * 60); // Convert to hours
          }, 0) / resolvedAlerts.length
        : 0;

    // Find oldest active alert
    const activeAlerts = alerts.filter((a: any) => a.status === 'active');
    const oldestActiveAlert =
      activeAlerts.length > 0
        ? activeAlerts.reduce((oldest: any, current: any) =>
            new Date(current.detectedAt) < new Date(oldest.detectedAt) ? current : oldest
          )
        : undefined;

    return {
      totalActive,
      totalAcknowledged,
      totalResolved,
      totalDismissed,
      bySeverity,
      byCategory,
      criticalAlerts,
      recentAlerts,
      avgResolutionTime,
      oldestActiveAlert,
    };
  },
});
